import { Box, Typography } from '@mui/material'
import React from 'react'
import { useSelector } from 'react-redux'
import { CategoryIcon } from '../../components/MachineTable/MachineIcon'
import { getControl } from '../../store/ControlById/selectors'
import { PartLog } from '../../types'
import { categoryStr } from '../../utils'
import { hexPinCode, toBitPins, toHms } from '../../utils/businessUtil'
import { dicebearUrl } from '../../utils/dicebear'

export type Props = {
	plog: PartLog
}

const clampPct = (n: number) => Math.max(0, Math.min(100, n))

// ラベル付きの小さなフィールド（折り返し前提）。
const Field = ({
	label,
	children,
}: React.PropsWithChildren<{ label: string }>) => (
	<Box
		sx={{
			display: 'inline-flex',
			gap: 0.5,
			alignItems: 'baseline',
			fontSize: 11,
			color: 'text.secondary',
			fontVariantNumeric: 'tabular-nums',
		}}
	>
		<Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
			{label}
		</Box>
		<span>{children}</span>
	</Box>
)

const LinePinView = ({ pin }: { pin?: number }) => {
	if (typeof pin !== 'number')
		return (
			<Box component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>
				-
			</Box>
		)
	return (
		<Box
			sx={{
				display: 'flex',
				gap: 0.5,
				alignItems: 'center',
				fontSize: 11,
				fontFamily: 'monospace',
				color: 'text.secondary',
				flexWrap: 'wrap',
				minWidth: 0,
			}}
		>
			<span>[{hexPinCode(pin)}]</span>
			<Box component="span" sx={{ wordBreak: 'break-all' }}>
				{toBitPins(pin)
					.map((b) => (b ? '1' : '0'))
					.join('')}
			</Box>
		</Box>
	)
}

export default function PartLogCard({ plog }: Props) {
	const control = useSelector((s) => getControl(s, plog.alog.controlId))
	const a = plog.alog
	const runRate = Math.floor((a.endRate || 0) - (a.startRate || 0))

	return (
		<Box sx={{ my: 1 }}>
			{/* 進捗ライン。paddingLeft(%)+width(%) が 100% を超えても溢れないよう clamp + hidden */}
			<Box sx={{ pl: `${clampPct(a.startRate || 0)}%`, overflow: 'hidden' }}>
				<Box
					sx={{
						width: `${clampPct(Math.abs(runRate))}%`,
						minHeight: 8,
						fontSize: 4,
						bgcolor: 'grey.500',
						color: 'common.white',
						borderRadius:
							plog.timing === 'start' ? '8px 0 0 8px' : '0 8px 8px 0',
					}}
				>
					{runRate}
				</Box>
			</Box>

			<Box
				className="plog-card"
				data-tag={a.tag}
				data-cancelled={a.cancelled}
				data-progress={a.progress}
				data-timing={plog.timing}
				sx={{
					border: '1px solid',
					borderColor: 'divider',
					borderRadius: 1,
					p: 1,
					display: 'flex',
					flexWrap: 'wrap',
					alignItems: 'center',
					gap: 1,
					ml: plog.timing === 'end' ? 2 : 0,
					mr: plog.timing === 'start' ? 2 : 0,
					opacity: a.cancelled ? 0.55 : 1,
				}}
			>
				<Box
					component="img"
					src={dicebearUrl(a.id)}
					alt=""
					sx={{
						width: 22,
						height: 22,
						borderRadius: 0.5,
						bgcolor: 'action.hover',
						flexShrink: 0,
					}}
				/>
				<CategoryIcon
					category={control.category}
					stepRate={6}
					style={{ width: '22px', height: '22px', flexShrink: 0 }}
				/>
				<Typography sx={{ fontWeight: 700, fontSize: 12.5 }}>
					{categoryStr[control.category]}
					{control.label}
				</Typography>

				<Box
					sx={{
						display: 'flex',
						flexWrap: 'wrap',
						gap: 1,
						alignItems: 'center',
					}}
				>
					<Field label="P">{toHms(plog.time)}</Field>
					<Field label="S">{toHms(plog.scheduleTime)}</Field>
					<Field label="C">{toHms(a.createdAt)}</Field>
					<Field label="U">{toHms(a.updatedAt)}</Field>
					<Field label="rate">{plog.rate}</Field>
					<Field label="run">{a.runningTime}s</Field>
				</Box>
				<LinePinView pin={plog.pin} />
			</Box>
		</Box>
	)
}
